import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import validate from './validate';
import PassInput from 'components/atoms/passInput';
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Avatar,
  CircularProgress
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockResetIcon from '@mui/icons-material/LockReset';
import { acceptInvitation as acceptInvitationApi, validateInvitation, resetPassword } from 'api'

const AcceptInvitation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { token } = params;
  const [open, setOpen] = useState(false);
  const [invitationStatus, setInvitationStatus] = useState(0);
  const [loading, setLoading] = useState(true);
  const [validationResponse, setValidationResponse] = useState(null);
  const [sendingResetEmail, setSendingResetEmail] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(validate),
    defaultValues: {
      password: ''
    }
  });

  // Validate the invitation token when component mounts
  useEffect(() => {
    const checkInvitationValidity = async () => {
      try {
        const response = await validateInvitation(token);
        setValidationResponse(response);
        setLoading(false);
      } catch (error) {
        console.error('Failed to validate invitation:', error);
        setValidationResponse({ valid: false });
        setLoading(false);
      }
    };

    checkInvitationValidity();
  }, [token]);

  const handleClose = () => {
    setOpen(false);
    if (invitationStatus === 1) {
      navigate('/login');
    }
  };

  const handleResetPassword = async () => {
    if (!validationResponse || !validationResponse.email) {
      return;
    }

    setSendingResetEmail(true);
    try {
      await resetPassword({ email: validationResponse.email });
      setResetEmailSent(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      console.error('Failed to send reset password email:', error);
    } finally {
      setSendingResetEmail(false);
    }
  };

  const submitInvitation = async (data) => {
    try {
      const response = await acceptInvitationApi({ password: data.password, key: token });
      
      if (response.status !== 'success') {
        setOpen(true);
        setInvitationStatus(-1);
        return;
      }
      
      setOpen(true);
      setInvitationStatus(1);
    } catch (error) {
      console.error('Failed to accept invitation:', error);
      setOpen(true);
      setInvitationStatus(-1);
    }
  };

  const handleAcceptInvitation = handleSubmit((data) => {
    submitInvitation(data);
  });

  // Show loading state while validating invitation
  if (loading) {
    return (
      <Container
        maxWidth="xs"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'calc(100vh - 200px)',
          py: 4
        }}
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Validating invitation...
        </Typography>
      </Container>
    );
  }

  // Invalid invitation token
  if (!validationResponse || !validationResponse.valid) {
    return (
      <Container
        maxWidth="xs"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'calc(100vh - 200px)',
          py: 4
        }}
      >
        <Paper
          elevation={3}
          sx={{
            width: {
              xs: 'calc(100% - 20px)',
              sm: 'calc(100% - 40px)',
              md: 450,
              lg: 450
            },
            maxWidth: {
              xs: 'none',
              md: 450
            },
            margin: 'auto',
            p: {
              xs: 2,
              md: 4
            },
            borderRadius: 2
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Avatar sx={{ m: '0 auto', bgcolor: 'error.main', mb: 1 }}>
              <ErrorOutlineIcon />
            </Avatar>
            <Typography
              variant="h4"
              color="error"
              sx={{ fontWeight: 700, mb: 1 }}
            >
              Invalid Invitation
            </Typography>
            <Typography variant="body1" color="text.secondary">
              This invitation link has already been used or is invalid.
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 3 }}>
            {validationResponse && validationResponse.email ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={sendingResetEmail ? <CircularProgress size={20} color="inherit" /> : <LockResetIcon />}
                onClick={handleResetPassword}
                disabled={sendingResetEmail || resetEmailSent}
                sx={{ mb: 2 }}
              >
                Reset Password
              </Button>
            ) : null}
            
            {resetEmailSent && (
              <Typography variant="body2" color="success.main" sx={{ mt: 2, mb: 2 }}>
                Password reset email sent successfully! You will be redirected to the login page.
              </Typography>
            )}
            
            <Button
              variant="outlined"
              onClick={() => navigate('/login')}
              sx={{ mt: 1 }}
            >
              Go to Login
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Valid invitation - show password creation form
  return (
    <Container
      maxWidth="xs"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 'calc(100vh - 200px)',
        py: 4
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: {
            xs: 'calc(100% - 20px)',
            sm: 'calc(100% - 40px)',
            md: 450,
            lg: 450
          },
          maxWidth: {
            xs: 'none',
            md: 450
          },
          margin: 'auto',
          p: {
            xs: 2,
            md: 4
          },
          borderRadius: 2
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Avatar sx={{ m: '0 auto', bgcolor: 'primary.main', mb: 1 }}>
            <PersonAddIcon />
          </Avatar>
          <Typography
            variant="h4"
            color="primary"
            sx={{ fontWeight: 700, mb: 1 }}
          >
            Welcome to Hive!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create your password to complete your account setup
          </Typography>
          {validationResponse && validationResponse.email && (
            <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
              Account: {validationResponse.email}
            </Typography>
          )}
        </Box>

        <Box component="form" onSubmit={handleAcceptInvitation} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please choose a strong password that is at least 8 characters long with a mix of uppercase and lowercase letters, numbers, and symbols.
          </Typography>

          <PassInput
            label="Password"
            name="password"
            control={control}
            error={errors.password}
            sx={{ width: '100%', mb: 2 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            disabled={isSubmitting}
            sx={{
              mt: 2,
              mb: 2,
              py: 1.2,
              fontWeight: 500
            }}
          >
            Create Account
          </Button>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              variant="text"
              color="primary"
              onClick={() => navigate('/login')}
              sx={{ textTransform: 'none' }}
            >
              Back to login
            </Button>
          </Box>
        </Box>
      </Paper>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 2,
            maxWidth: 400
          }
        }}
      >
        <DialogTitle id="alert-dialog-title" sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {invitationStatus === 1 ? (
              <CheckCircleOutlineIcon color="success" sx={{ mr: 1, fontSize: 28 }} />
            ) : (
              <ErrorOutlineIcon color="error" sx={{ mr: 1, fontSize: 28 }} />
            )}
            {invitationStatus === 1 ? "Account Created Successfully" : "Account Creation Failed"}
          </Box>
        </DialogTitle>

        <DialogContent>
          {invitationStatus === 1 ? (
            <Typography variant="body1">
              Your account has been created successfully. You can now log in to your account using your email and the password you just created.
            </Typography>
          ) : (
            <Typography variant="body1">
              We couldn't create your account. This may be because the invitation link has expired or is invalid. Please contact your administrator for a new invitation.
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ pt: 2 }}>
          <Button
            onClick={handleClose}
            variant="contained"
            color={invitationStatus === 1 ? "primary" : "error"}
            autoFocus
            sx={{ textTransform: 'none' }}
          >
            {invitationStatus === 1 ? "Go to Login" : "Try Again"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AcceptInvitation;